import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef } from "react";

const PHOTOS = [
  { src: "/images/magazzino-3.webp", label: "Corsie Organizzate", sub: "Ogni referenza al suo posto" },
  { src: "/images/magazzino-2.webp", label: "Scaffalature Industriali", sub: "Stock sempre pronto al prelievo" },
  { src: "/images/magazzino-4.webp", label: "Reparto Conserve", sub: "Rotazione continua delle merci" },
  { src: "/images/magazzino-1.webp", label: "Pronta Consegna", sub: "Bancali preparati per la spedizione" },
];

export default function Warehouse() {
  const trackRef = useRef(null);

  const scroll = (dir) => {
    trackRef.current?.scrollBy({ left: dir * (trackRef.current.clientWidth * 0.75), behavior: "smooth" });
  };

  return (
    <section id="magazzino" data-testid="warehouse-section" className="py-24 md:py-32 bg-[#06152d] text-white overflow-hidden relative">
      <div className="absolute inset-0 df-pattern-gold" />
      <div className="relative max-w-6xl mx-auto px-6 space-y-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="df-ornament text-xs uppercase font-mono-df tracking-widest"
        >
          <span>Il Nostro Magazzino · Rozzano (MI)</span>
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display text-4xl sm:text-5xl md:text-6xl font-normal tracking-tight max-w-3xl"
        >
          Logistica che non lascia <span className="italic text-[#d4af37]">nulla al caso.</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-sm text-white/60 max-w-xl"
        >
          Foto reali del nostro magazzino: merce stoccata con ordine, pronta per arrivare puntuale nella tua attività.
        </motion.p>
      </div>

      <div className="relative mt-12">
        <div
          ref={trackRef}
          data-testid="warehouse-gallery"
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory px-6 md:px-[max(1.5rem,calc((100vw-72rem)/2))] pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {PHOTOS.map((p, i) => (
            <motion.figure
              key={p.src}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.2, 0.7, 0, 1] }}
              data-testid={`warehouse-photo-${i + 1}`}
              className="snap-center shrink-0 w-[82vw] sm:w-[55vw] lg:w-[38vw] group"
            >
              <div className="relative rounded-3xl overflow-hidden border border-[#d4af37]/20 shadow-2xl aspect-[3/4] sm:aspect-[4/5]">
                <img
                  src={p.src}
                  alt={p.label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#06152d]/80 via-transparent to-transparent" />
                <figcaption className="absolute bottom-0 inset-x-0 p-6">
                  <span className="text-[10px] uppercase font-mono-df tracking-widest text-[#d4af37]">{p.label}</span>
                  <p className="font-display text-lg text-white">{p.sub}</p>
                </figcaption>
              </div>
            </motion.figure>
          ))}
        </div>

        <div className="hidden md:flex justify-end gap-3 max-w-6xl mx-auto px-6 mt-4">
          <button data-testid="warehouse-prev" onClick={() => scroll(-1)} aria-label="Precedente"
            className="w-11 h-11 rounded-full border border-white/20 text-white/70 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors flex items-center justify-center">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button data-testid="warehouse-next" onClick={() => scroll(1)} aria-label="Successiva"
            className="w-11 h-11 rounded-full border border-white/20 text-white/70 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors flex items-center justify-center">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
}
